// Phase 6 — Track B T-B3. API endpoint constants.
//
// One named constant per route the Phase 6 web SPA consumes. Keeping these
// in one place makes the contract drift between client and server easy to
// audit (and easy to refactor when v2.x renames a route).

export const endpoints = {
  // Auth
  authRegister: '/api/auth/register',
  authLogin: '/api/auth/login',
  authLogout: '/api/auth/logout',
  authMe: '/api/auth/me',
  authToken: '/api/auth/token',
  authTokenRefresh: '/api/auth/token/refresh',
  authChangePassword: '/api/auth/change-password',
  authDeleteAccount: '/api/auth/account',
  authDevices: '/api/auth/devices',
  authDevicesAllButCurrent: '/api/auth/devices/all-but-current',

  // Read-only routes
  dashboard: '/api/dashboard',
  alerts: '/api/alerts',
  dailyDeck: '/api/daily-deck',
  paymentsList: '/api/modules/payments/payments',
  datesList: '/api/modules/dates/events',

  // Phase 7 — `/api/users/me/*`
  usersMePreferences: '/api/users/me/preferences',
  usersMeTimezone: '/api/users/me/timezone',

  // Payments — Gmail OAuth (Phase 3 backend; Phase 7 D3 SPA consumer)
  paymentsEmailStatus: '/api/modules/payments/email/status',
  paymentsEmailConnect: '/api/modules/payments/email/connect',
  paymentsEmailScan: '/api/modules/payments/email/scan',

  // Payments — statements (Phase 3 backend; Phase 7 D4 SPA consumer)
  paymentsStatements: '/api/modules/payments/statements',

  // Payments — categorisation (Phase 3 backend; Phase 7 D5 SPA consumer)
  paymentsCategorisationsReview: '/api/modules/payments/categorisations/review',
  paymentsCategories: '/api/modules/payments/categories',

  // Payments — recurring (Phase 3 backend; Phase 7 D5 SPA consumer)
  paymentsRecurring: '/api/modules/payments/recurring',

  // Payments — insights / summary / anomalies (Phase 3 backend; Phase 7.5
  // D3 SPA consumer). Surfaces the AI weekly narrative + spend analytics +
  // anomaly detections on the new /insights route + the daily-deck cards.
  paymentsInsights: '/api/modules/payments/insights',
  paymentsInsightsSummary: '/api/modules/payments/insights/summary',
  paymentsAnomalies: '/api/modules/payments/anomalies',
} as const;

/**
 * Phase 7 — per-id write surfaces. Kept as helpers (not entries on the
 * `endpoints` const) so `EndpointKey = keyof typeof endpoints` stays a
 * plain string-valued union.
 */
export const endpointPaths = {
  paymentsItem: (id: string): string => `/api/modules/payments/payments/${encodeURIComponent(id)}`,
  datesItem: (id: string): string => `/api/modules/dates/events/${encodeURIComponent(id)}`,
  authDeviceItem: (id: string): string => `/api/auth/devices/${encodeURIComponent(id)}`,
  /**
   * Phase 7 D5 — per-prediction correction route.
   * `POST /api/modules/payments/categorisations/:predictionId/correct`
   */
  paymentsCategorisationCorrect: (predictionId: string): string =>
    `/api/modules/payments/categorisations/${encodeURIComponent(predictionId)}/correct`,
  /**
   * Phase 7 D5 — per-recurring update route.
   * `PATCH /api/modules/payments/recurring/:id`
   */
  paymentsRecurringItem: (id: string): string =>
    `/api/modules/payments/recurring/${encodeURIComponent(id)}`,
} as const;

export type EndpointKey = keyof typeof endpoints;
